import CalendarViewDayIcon from "@mui/icons-material/CalendarViewDay";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import {
  Box,
  BoxProps,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import { Spacer, Toolbar } from "common";
import { NAVIGATE, VIEWS } from "common/components/CalendarPro";
import DateRangeSelector from "components/DateRangeSeletor/DateRangeSeletor";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { Moment } from "moment";
import { Fragment, ReactNode, useEffect, useState } from "react";
import EditButton from "../Editor/EditButton";
import EditorToolbar from "../Editor/EditorToolbar";

export interface CalendarToolbarProps {
  label?: string | null;
  view: VIEWS;
  views: VIEWS[];
  onNavigate: (
    type: NAVIGATE,
    d?: Moment,
    noDelay?: boolean,
    _view?: VIEWS
  ) => void;
  onView: (_view: VIEWS) => void;
  buttons?: any[] | ReactNode;
  customToolbar: any;
  headerContent?: ReactNode;
  sx?: BoxProps["sx"];
  showRefreshButton?: boolean;
  refresh?: Function;
  date?: Moment;
}

export function setCalendarLoading(isLoading: boolean) {
  let event = new CustomEvent("setCalendarLoading", {
    detail: {
      isLoading,
    },
  });
  document.dispatchEvent(event);
}

export default function CalendarToolbar({
  label,
  view,
  views,
  onView,
  onNavigate,
  buttons,
  customToolbar,
  refresh,
  sx,
  headerContent,
  showRefreshButton = true,
  date,
}: CalendarToolbarProps) {
  const theme = useTheme();
  const { hasPermissions, canEditItem } = useAccess();
  const [loading, setLoading] = useState(false);

  function handleSetLoading({
    detail: { isLoading },
  }: {
    detail: { isLoading: boolean };
  }) {
    setLoading(isLoading);
  }
  useEffect(() => {
    document.addEventListener("setCalendarLoading", handleSetLoading as any);
    return () =>
      document.removeEventListener(
        "setCalendarLoading",
        handleSetLoading as any
      );
  }, []);

  return (
    <Box
      sx={{
        pt: 1,
        position: "relative",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <Toolbar
        sx={{ gap: 1, px: 1, flexWrap: "wrap", justifyContent: "center" }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton onClick={() => onNavigate(NAVIGATE.PREVIOUS)}>
            <ChevronLeftIcon />
          </IconButton>
          <DateRangeSelector
            sx={{ minWidth: 180, minHeight: 37 }}
            value={date}
            view={view}
            onChange={(startOf, newView) => {
              onNavigate(NAVIGATE.DATE, startOf, true, newView);
            }}
          />
          <IconButton onClick={() => onNavigate(NAVIGATE.NEXT)}>
            <ChevronRightIcon />
          </IconButton>
        </Toolbar>
        <ToggleButtonGroup
          exclusive
          onChange={(evt, v) => onView(v)}
          value={view}
          size="small"
        >
          <ToggleButton
            title={I18n.t("Calendar.monthView")}
            value={VIEWS.MONTH}
          >
            <CalendarViewMonthIcon />
          </ToggleButton>
          <ToggleButton title={I18n.t("Calendar.weekView")} value={VIEWS.WEEK}>
            <CalendarViewWeekIcon />
          </ToggleButton>
          <ToggleButton title={I18n.t("Calendar.dayView")} value={VIEWS.DAY}>
            <CalendarViewDayIcon />
          </ToggleButton>
          <ToggleButton
            title={I18n.t("Calendar.agendaView")}
            value={VIEWS.AGENDA}
          >
            <ViewAgendaIcon />
          </ToggleButton>
        </ToggleButtonGroup>
        <Toolbar sx={{ gap: 1, flex: 1, justifyContent: "flex-end" }}>
          {headerContent}
          {buttons &&
            (!Array.isArray(buttons)
              ? buttons
              : buttons.map((button, index) => (
                  <Fragment key={index}>
                    <Button
                      onClick={button.onClick}
                      variant="outlined"
                      title={button.title}
                    >
                      {button.title}
                    </Button>
                    {buttons.length - 1 > index && <Spacer w={0.5} />}
                  </Fragment>
                )))}
        </Toolbar>
        {showRefreshButton && (
          <IconButton
            disabled={loading}
            onClick={refresh ? () => refresh() : undefined}
          >
            {loading ? (
              <CircularProgress size={20} color="primary" />
            ) : (
              <RefreshIcon />
            )}
          </IconButton>
        )}
        {hasPermissions(["event.editany"]) && <EditButton />}
      </Toolbar>

      {customToolbar}

      <EditorToolbar />
      {loading && (
        <LinearProgress
          sx={{ position: "absolute", left: 0, right: 0, bottom: 0 }}
        />
      )}
    </Box>
  );
}
